import React, { useState, useRef } from 'react';
import TagInput from '../components/TagInput';

function parseCards(text) {
  return text.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length === 0) return [];
  const firstLine = lines[0].toLowerCase();
  const hasHeader = firstLine.includes('body') || firstLine.includes('text') || firstLine.includes('card');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  const cards = [];
  for (const line of dataLines) {
    if (!line.trim()) continue;
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { fields.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    fields.push(current.trim());
    const body = fields[0]?.trim();
    const tagField = fields[1]?.trim() || '';
    const tags = tagField ? tagField.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) : [];
    if (body) cards.push({ body, tags });
  }
  return cards;
}

export default function Import() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState(null); // 'text', 'csv', or 'paste'
  const [preview, setPreview] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const txtRef = useRef();
  const csvRef = useRef();
  const tagInputRef = useRef();

  const handleFile = (e, fileMode) => {
    const f = e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setError('');
    setSuccess(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      if (fileMode === 'csv') {
        const parsed = parseCSV(text);
        setPreview(parsed);
        setMode('csv');
      } else {
        const parsed = parseCards(text);
        setPreview(parsed);
        setMode('text');
      }
      setFile(f);
    };
    reader.readAsText(f);
  };

  const handlePastePreview = () => {
    const parsed = parseCards(pasteText);
    setPreview(parsed);
    setMode('paste');
  };

  const handleSubmit = async () => {
    if (!preview.length) return;
    setSaving(true);
    setError('');
    try {
      let res;
      if (mode === 'csv') {
        res = await fetch('/api/cards/bulk-csv', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cards: preview })
        });
      } else {
        const finalTags = tagInputRef.current ? tagInputRef.current.flush() : tags;
        const text = mode === 'paste' ? pasteText : preview.join('\n\n');
        res = await fetch('/api/cards/bulk', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, tags: finalTags })
        });
      }
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.created);
        setFile(null);
        setPreview([]);
        setMode(null);
        setPasteText('');
        setTags([]);
      } else {
        setError(data.error || 'Import failed.');
      }
    } catch {
      setError('Connection error. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFile(null); setPreview([]); setMode(null);
    setError(''); setSuccess(null); setPasteText(''); setTags([]);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="page-header">
        <h1>Import</h1>
        <div className="subtitle">Upload a file or paste cards to add them to your box.</div>
      </div>

      {success !== null ? (
        <div style={{ background: 'var(--accent)', color: 'white', padding: '0.75rem 1rem', borderRadius: 'var(--radius)', marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>
          {success} card{success !== 1 ? 's' : ''} imported.{' '}
          <button onClick={reset} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'sans-serif' }}>
            Import more
          </button>
        </div>
      ) : mode === 'paste' && preview.length > 0 ? (
        <>
          <div className="bulk-preview" style={{ marginBottom: '1rem' }}>
            <strong>{preview.length} card{preview.length !== 1 ? 's' : ''} detected</strong>
            {preview.slice(0, 3).map((p, i) => (
              <div key={i} style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text)' }}>
                <span style={{ whiteSpace: 'pre-wrap' }}>{p.length > 120 ? p.slice(0, 120) + '…' : p}</span>
              </div>
            ))}
            {preview.length > 3 && (
              <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>+ {preview.length - 3} more</div>
            )}
          </div>
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="form-label">Apply tags to all (optional)</label>
            <TagInput ref={tagInputRef} value={tags} onChange={setTags} />
          </div>
          {error && <p style={{ color: 'var(--no)', fontFamily: 'sans-serif', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ fontSize: '1rem' }}>
              {saving ? 'Importing…' : `Add ${preview.length} card${preview.length !== 1 ? 's' : ''} to box`}
            </button>
            <button className="btn btn-secondary" onClick={reset}>Start over</button>
          </div>
        </>
      ) : !file ? (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <button className="btn btn-primary" onClick={() => txtRef.current.click()} style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}>
              Upload .txt file
            </button>
            <button className="btn btn-secondary" onClick={() => csvRef.current.click()} style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}>
              Upload .csv file
            </button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontFamily: 'sans-serif', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            <strong>.txt</strong> — one blank line between cards.<br />
            <strong>.csv</strong> — two columns: body, tags (exported from Gleaner or formatted manually).
          </p>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1.5rem' }}>
            <p style={{ fontFamily: 'sans-serif', fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
              Or type or paste directly. Separate cards with a blank line between them.
            </p>
            <textarea
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              rows={8}
              style={{ width: '100%', fontFamily: 'sans-serif', fontSize: '0.95rem', padding: '0.75rem', borderRadius: 'var(--radius)', border: '1px solid var(--border)', resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Type or paste cards here..."
            />
        <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Apply tags to all (optional)</label>
              <TagInput ref={tagInputRef} value={tags} onChange={setTags} />
            </div>
            <button
              className="btn btn-primary"
              onClick={handlePastePreview}
              disabled={!pasteText.trim()}
              style={{ marginTop: '0.5rem' }}
            >
              Preview cards
            </button>
          </div>

          <input ref={txtRef} type="file" accept=".txt" style={{ display: 'none' }} onChange={e => handleFile(e, 'text')} />
          <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFile(e, 'csv')} />
        </>
      ) : (
        <>
          <div className="bulk-preview" style={{ marginBottom: '1rem' }}>
            <strong>{preview.length} card{preview.length !== 1 ? 's' : ''} detected</strong>
            {' '}from <em>{file.name}</em>
            {preview.slice(0, 3).map((p, i) => (
              <div key={i} style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text)' }}>
                {mode === 'csv' ? (
                  <span>
                    {p.body.length > 100 ? p.body.slice(0, 100) + '…' : p.body}
                    {p.tags.length > 0 && <span style={{ color: 'var(--accent)', marginLeft: '0.5rem' }}>[{p.tags.join(', ')}]</span>}
                  </span>
                ) : (
                  <span style={{ whiteSpace: 'pre-wrap' }}>{p.length > 120 ? p.slice(0, 120) + '…' : p}</span>
                )}
              </div>
            ))}
            {preview.length > 3 && (
              <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>+ {preview.length - 3} more</div>
            )}
          </div>
          {mode === 'text' && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">Apply tags to all (optional)</label>
              <TagInput ref={tagInputRef} value={tags} onChange={setTags} />
            </div>
          )}
          {error && <p style={{ color: 'var(--no)', fontFamily: 'sans-serif', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</p>}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ fontSize: '1rem' }}>
              {saving ? 'Importing…' : `Add ${preview.length} card${preview.length !== 1 ? 's' : ''} to box`}
            </button>
            <button className="btn btn-secondary" onClick={reset}>Choose a different file</button>
          </div>
        </>
      )}
    </div>
  );
}
